// src/features/booking-history/detail/DobField.tsx
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/formatDate";
import { startOfMonth, startOfToday } from "date-fns";
import { CalendarDays } from "lucide-react";
import { useState } from "react";

type DobFieldProps = {
  label: string;
  required?: boolean;
  placeholder?: string;
  value?: Date;
  className?: string;
  error?: string;
  onSelect: (value: Date) => void;
};

export function DobField({
  label,
  required,
  placeholder = "Select date",
  value,
  className,
  error,
  onSelect,
}: DobFieldProps) {
  const [open, setOpen] = useState(false);

  const minMonth = startOfMonth(new Date(1900, 0));
  const maxMonth = startOfMonth(startOfToday());
  const [month, setMonth] = useState(() =>
    startOfMonth(value ?? startOfToday()),
  );

  const isPrevDisabled = month <= minMonth;
  const isNextDisabled = month >= maxMonth;

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-bold tracking-wide text-field">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="outline"
              className={cn(
                className,
                "justify-between hover:bg-primary-soft",
                !value && "text-border!",
                error && "border-destructive ring-3 ring-destructive/20",
                open && "bg-accent!",
              )}
            >
              <span className="min-w-0 flex-1 truncate text-left">
                {value ? formatDate(value) : placeholder}
              </span>
              <CalendarDays
                size={16}
                className="text-secondary-foreground"
                aria-hidden="true"
              />
            </Button>
          }
        />
        <PopoverContent
          align="start"
          className="w-auto rounded-2xl p-2 shadow-xl"
        >
          <Calendar
            mode="single"
            selected={value}
            captionLayout="dropdown"
            month={month}
            onMonthChange={setMonth}
            startMonth={minMonth}
            endMonth={maxMonth}
            disabled={{ after: startOfToday() }}
            onSelect={(date) => {
              if (!date) return;
              onSelect(date);
              setMonth(startOfMonth(date));
              setOpen(false);
            }}
            className="w-full p-0 bg-transparent"
            classNames={{
              button_previous: cn(
                "bg-transparent border flex items-center justify-center border-border hover:bg-gray-100 h-10 w-10 md:h-8 md:w-8 rounded-full",
                isPrevDisabled && "opacity-50 pointer-events-none",
              ),
              button_next: cn(
                "bg-transparent border flex items-center justify-center border-border hover:bg-gray-100 h-10 w-10 md:h-8 md:w-8 rounded-full",
                isNextDisabled && "opacity-50 pointer-events-none",
              ),
            }}
          />
        </PopoverContent>
      </Popover>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
